<template>
  <BaseSlideOverModal
    v-model:is-modal-open="localOpen"
    width="xl"
    :title="$t('navigation.template_dashboard_title_main')"
    @close="close"
  >
    <template #body>
      <div class="flex h-full flex-1 flex-col gap-4 overflow-y-auto">
        <div class="flex flex-col gap-2">
          <div
            v-if="canManageFavorite"
            class="flex flex-row items-center gap-2"
          >
            <div class="shrink-0">
              <FontAwesomeIcon
                :icon="['fas', 'star']"
                fixed-width
                class="text-yellow-300"
                size="lg"
              />
            </div>
            <div class="flex-1">
              <h2 class="text-lg font-semibold capitalize-first">
                {{ $t('attributes.starred') }}
              </h2>
            </div>
          </div>
          <div class="rounded border">
            <div
              v-if="
                !Array.isArray(favoriteUserDashboards) ||
                !favoriteUserDashboards.length
              "
              class="p-2"
            >
              {{ $t('global.click_add_button') }}
            </div>
            <draggable
              v-model="favoriteUserDashboards"
              class="divide-y divide-gray-100"
              @update="handleDrag"
            >
              <template #item="{ element }">
                <div class="flex cursor-move flex-row items-center gap-2 p-2">
                  <div class="shrink-0">
                    <FontAwesomeIcon
                      :icon="['fal', 'bars']"
                      fixed-width
                      class="cursor-pointer text-primary"
                    />
                  </div>
                  <div class="flex-1">
                    <h3
                      class="text-sm font-semibold leading-6 text-gray-900 dark:border-white dark:bg-slate-700 dark:text-white"
                    >
                      {{ element.name[$i18n.locale] }}
                    </h3>
                  </div>
                  <div class="shrink-0">
                    <button
                      v-if="canManageFavorite"
                      @click="removeFavorite(element)"
                    >
                      <FontAwesomeIcon
                        :icon="['fal', 'times-circle']"
                        class="text-theme-danger"
                        fixed-width
                      />
                    </button>
                  </div>
                </div>
              </template>
            </draggable>
          </div>
        </div>

        <div v-if="canManageFavorite" class="flex flex-col gap-2">
          <div class="flex flex-row items-center gap-2">
            <div class="shrink-0">
              <FontAwesomeIcon :icon="['fal', 'star']" fixed-width size="lg" />
            </div>
            <div class="flex-1">
              <h2 class="text-lg font-semibold capitalize-first">
                {{ $t('global.others') }}
              </h2>
            </div>
          </div>
          <div
            class="divide-y divide-gray-100 rounded border dark:divide-white"
          >
            <div v-for="template in otherUserDashboards" :key="template.id">
              <div class="flex flex-row items-center gap-2 p-2">
                <div class="flex-1">
                  <h3 class="text-sm font-semibold leading-6 text-gray-900">
                    {{ template.name[$i18n.locale] }}
                  </h3>
                </div>
                <div class="shrink-0">
                  <button @click="addFavorite(template)">
                    <FontAwesomeIcon
                      :icon="['fal', 'plus-circle']"
                      class="text-primary"
                      fixed-width
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseSlideOverModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseSlideOverModal from '@c/addf-package/components/modal/BaseSlideOverModal.vue'
import draggable from 'vuedraggable'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import DashboardTypes from '@/assets/enums/dashboard/DashboardTypes'

export default {
  name: 'DashboardModal',
  components: {
    FontAwesomeIcon,
    BaseSlideOverModal,
    draggable
  },
  props: {
    open: {
      type: Boolean,
      required: true
    },
    typeDashboardAvailable: {
      type: Array,
      required: false,
      default: () => Object.values(DashboardTypes)
    }
  },
  emits: ['close', 'status-dashboard-change', 'update:open'],
  computed: {
    ...mapGetters({
      metadata: 'metadata/getInitialMetadata',
      templateDashboard: 'metadata/getTemplateDashboards'
    }),
    localOpen: {
      get() {
        return this.open
      },
      set(value) {
        this.$emit('update:open', value)
      }
    },
    canManageFavorite() {
      return this.typeDashboardAvailable.length > 1
    },
    favoriteUserDashboards() {
      if (this.typeDashboardAvailable.length === 1) {
        return this.metadata?.models?.template_dashboards
          ?.filter((dashboard) =>
            this.typeDashboardAvailable.includes(dashboard.type)
          )
          ?.sort((a, b) => {
            if (
              a.pivot.local_position === null ||
              b.pivot.local_position === null
            ) {
              return a.pivot.local_position === null ? 1 : -1
            }

            return a.pivot.local_position - b.pivot.local_position
          })
      } else {
        return this.metadata?.models?.template_dashboards
          ?.filter(
            (dashboard) =>
              dashboard.pivot.favorite === 1 ||
              dashboard.pivot.favorite === true
          )
          ?.sort((a, b) => {
            if (a.pivot.position === null || b.pivot.position === null) {
              return a.pivot.position === null ? 1 : -1
            }

            return a.pivot.position - b.pivot.position
          })
      }
    },
    otherUserDashboards() {
      return this.metadata?.models?.template_dashboards
        ?.filter(
          (dashboard) =>
            dashboard.pivot.favorite === 0 ||
            dashboard.pivot.favorite === false ||
            dashboard.pivot.favorite == null
        )
        ?.sort((a, b) => {
          if (a.pivot.position === null || b.pivot.position === null) {
            return a.pivot.position === null ? 1 : -1
          }

          return a.pivot.position - b.pivot.position
        })
    }
  },
  methods: {
    ...mapActions({
      processUpdateTemplateDashboardUserByIdAction:
        'dashboard/updateTemplateDashboardUserById',
      processRefreshSpecificInitialMetadataModelsAction:
        'metadata/refreshSpecificInitialMetadataModels',
      processUpdateTemplateDashboardUserPositionAction:
        'dashboard/updateTemplateDashboardUserPosition'
    }),
    close() {
      this.$emit('close')
    },
    handleDrag(event) {
      const { newIndex, oldIndex } = event
      this.processUpdateTemplateDashboardUserPositionAction({
        oldIndex: oldIndex,
        newIndex: newIndex,
        onlyFavorite: this.canManageFavorite,
        dashboardType: this.canManageFavorite
          ? null
          : this.typeDashboardAvailable[0]
      }).then(() => {
        this.processRefreshSpecificInitialMetadataModelsAction({
          model: 'template_dashboards'
        })
      })
    },
    removeFavorite(element) {
      this.updateFavoriteStatus(element, false)
    },

    addFavorite(element) {
      this.updateFavoriteStatus(element, true)
    },

    updateFavoriteStatus(element, favoriteStatus) {
      const newFavoriteStatus = favoriteStatus ? 1 : 0

      this.processUpdateTemplateDashboardUserByIdAction({
        id: element.pivot.id,
        data: {
          template_dashboard_id: element.pivot.template_dashboard_id,
          user_id: element.pivot.user_id,
          position: element.pivot.position,
          favorite: newFavoriteStatus
        }
      }).then(() => {
        this.processRefreshSpecificInitialMetadataModelsAction({
          model: 'template_dashboards'
        })

        if (favoriteStatus) {
          this.$router.push({
            name: 'dashboard_page_route',
            params: {
              id: element.pivot.template_dashboard_id
            }
          })
          this.$h.toastSuccess(
            `${element.name[this.$i18n.locale]} ${this.$t(
              'global.add_to_favorite'
            )}`
          )
        } else {
          if (this.favoriteUserDashboards[0]) {
            this.$router.push({
              name: 'dashboard_page_route',
              params: {
                id: this.favoriteUserDashboards[0].id
              }
            })
          } else {
            this.$router.push({
              name: 'dashboard_page_route',
              params: {
                id: this.templateDashboard[0].id
              }
            })
          }
          this.$h.toastSuccess(
            `${element.name[this.$i18n.locale]} ${this.$t(
              'global.not_add_to_favorite'
            )}`
          )
        }
      })
    }
  }
}
</script>
